/**
 * Les GESTES DE RELECTURE d'un contenu rédigé — fiche, capsule, erreur,
 * chapitre (chantier « manuel vivant », Φ-MV-2, MV-CNT-05, AD-MV-19).
 *
 * Deux familles de gestes, deux axes (voir `lib/savoir-format.ts`) :
 *
 *  - RELIRE — « Relu, rien à changer » : `POST …/relecture`. Le contenu reste
 *    en service ; il cesse d'être « produit par agent — non relu », avec le nom
 *    et la date de qui l'a relu. Le geste se défait — « Remettre à relire »,
 *    `DELETE …/relecture` : un « relu » posé par mégarde ferait dire à la
 *    console ce que personne n'a fait.
 *  - CHANGER LE SERVICE — sortir pour reprise, valider, publier : `POST
 *    …/statut`. Ces gestes changent ce que l'ÉLÈVE reçoit : ils se confirment.
 *
 * ─── Un seul bouton plein (MV-UX-02) ────────────────────────────────────────
 *
 * Le bouton plein est la relecture tant qu'elle reste à faire — c'est le geste
 * attendu. Les changements de statut sont discrets. Quand l'un d'eux demande
 * confirmation, la barre CÈDE la place à la question : « Confirmer » devient
 * alors le seul bouton plein, et on ne peut pas confirmer par mégarde un geste
 * pendant qu'on en regarde un autre.
 *
 * ─── Ce que ce composant n'offre pas ────────────────────────────────────────
 *
 * Corriger. Une fiche se corrige dans son dossier de contenu et revient par
 * l'import, en version neuve ; une capsule se corrige dans son script, et la
 * chaîne vidéo la refait. La console ne récrit pas un contenu publié en place.
 *
 * Un refus du serveur (transition non admise, chapitre dont une erreur n'a pas
 * son remède) est affiché TEL QUEL : c'est le serveur qui sait pourquoi.
 */
import type { ContentStatus, SorteSavoir } from '@silex/shared';
import { useState } from 'react';

import { api } from '../api/client';
import { useRoleCourant } from '../auth/AuthContext';
import { actionsDeStatut, libelleMarquerRelu, type ActionDeStatut } from '../lib/savoir-format';
import { messageErreur } from '../lib/useAsync';

export type CibleSavoir = SorteSavoir | 'chapitres';

export interface ActionsSavoirProps {
  readonly cible: CibleSavoir;
  readonly code: string;
  /** Le statut de service. Absent pour une erreur : elle n'a pas de statut. */
  readonly statut?: ContentStatus;
  /** `false` : la relecture reste à faire. Absent pour un chapitre : il ne se relit pas. */
  readonly relu?: boolean;
  /** Appelé après toute écriture réussie : au parent de recharger ce qu'il montre. */
  readonly surChangement: () => void;
  /**
   * L4 — appelé À LA PLACE de `surChangement` après « Relu » : la page de
   * relecture passe au contenu suivant. Absent : comme avant.
   */
  readonly surRelu?: () => void;
}

export function ActionsSavoir({
  cible,
  code,
  statut,
  relu,
  surChangement,
  surRelu,
}: ActionsSavoirProps) {
  const [enCours, setEnCours] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [aConfirmer, setAConfirmer] = useState<ActionDeStatut | null>(null);

  /* Un chapitre ne se relit pas : ce sont ses contenus qui se relisent. */
  const aRelire: SorteSavoir | null = cible !== 'chapitres' && relu === false ? cible : null;
  /* Un contenu retiré ne revient pas : on ne le remet pas non plus à relire. */
  const dejaRelu: SorteSavoir | null =
    cible !== 'chapitres' && relu === true && statut !== 'retire' ? cible : null;
  /* Publier, retirer, remettre en service : ce que l'ÉLÈVE reçoit change pour
     toute la plateforme. Réservé aux admins (décision du fondateur, 14 octobre
     2026) ; le serveur refuse de toute façon un tuteur. La relecture reste au
     tuteur. */
  const admin = useRoleCourant() === 'admin';
  const actions =
    !admin || statut === undefined || cible === 'erreurs' ? [] : actionsDeStatut(statut, cible);

  const jouer = async (
    ecriture: () => Promise<unknown>,
    apres: () => void = surChangement,
  ): Promise<void> => {
    setEnCours(true);
    setRefus(null);
    try {
      await ecriture();
      setAConfirmer(null);
      apres();
    } catch (e) {
      /* Refusé : la question se referme. Le refus dit pourquoi ; le geste
         reste offert dans la barre, une fois la cause levée. */
      setAConfirmer(null);
      setRefus(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  if (aRelire === null && dejaRelu === null && actions.length === 0 && refus === null) return null;

  return (
    <div className="actions-savoir" data-testid="actions-savoir">
      {refus !== null ? (
        <div className="callout callout--rare" role="alert" data-testid="actions-savoir-refus">
          <strong>Action refusée.</strong> {refus}
        </div>
      ) : null}

      {aConfirmer !== null && cible !== 'erreurs' ? (
        <div className="callout callout--calm actions-savoir__confirmation" role="group">
          <p>
            <strong>{aConfirmer.libelle} ?</strong> {aConfirmer.confirmation}
          </p>
          <div className="actions-savoir__barre">
            <button
              type="button"
              className="btn btn--sm"
              disabled={enCours}
              data-testid="actions-savoir-confirmer"
              onClick={() =>
                void jouer(() => api.changerStatutSavoir(cible, code, { statut: aConfirmer.vers }))
              }
            >
              Confirmer
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={enCours}
              onClick={() => setAConfirmer(null)}
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="actions-savoir__barre">
          {aRelire !== null ? (
            <button
              type="button"
              className="btn btn--sm"
              disabled={enCours}
              data-testid="actions-savoir-relu"
              onClick={() =>
                void jouer(() => api.marquerSavoirRelu(aRelire, code), surRelu ?? surChangement)
              }
            >
              {libelleMarquerRelu(aRelire)}
            </button>
          ) : null}
          {dejaRelu !== null ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={enCours}
              data-testid="actions-savoir-a-relire"
              onClick={() => void jouer(() => api.remettreSavoirARelire(dejaRelu, code))}
            >
              Remettre à relire
            </button>
          ) : null}
          {actions.map((a) => (
            <button
              key={a.vers}
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={enCours}
              data-testid={`actions-savoir-vers-${a.vers}`}
              onClick={() => {
                setRefus(null);
                setAConfirmer(a);
              }}
            >
              {a.libelle}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
